import { RadioGroup } from "@headlessui/react";
import classNames from "classnames";
import omit from "lodash/omit";
import moment from "moment-timezone";
import Form from "rc-field-form";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../../../../components/Button";
import Field from "../../../../../../components/Field";
import BaseSettingsFormContent from "../../../../../../components/Form/BaseSettingsFormContent";
import Checkbox from "../../../../../../components/Inputs/Checkbox";
import DateInput from "../../../../../../components/Inputs/DateInput";
import Switch from "../../../../../../components/Inputs/Switch";
import { generateErrorsConfigForForm } from "../../../../../../utils/helpers/errors";
import { useModuleAccess } from "../../../../../../utils/hooks/access";
import { useIsMasterCompany } from "../../../../../../utils/hooks/company";
import { useIsFieldDisabled } from "../../../../helpers/useIsFieldDisabled";

function Base({ form: parentForm, mode, close, values, error, hidden }) {
    const { t } = useTranslation();

    const isMasterCompany = useIsMasterCompany();
    const { access } = useModuleAccess("settings.payPolicies.calculationGroup");
    const disabled = useIsFieldDisabled({ ...access, mode });

    let form;
    const [componentForm] = Form.useForm();
    if (parentForm) {
        form = parentForm;
    } else {
        form = componentForm;
    }

    useEffect(() => {
        form.resetFields();
        form.setFieldsValue({
            ...values,
            effectiveDate: values?.effectiveDate ? moment(values.effectiveDate).toDate() : undefined,
            status: values?.status || "active",
        });
    }, [form, values]);

    useEffect(() => {
        const fieldErrors = generateErrorsConfigForForm(
            ["code", "description", "status", "effectiveDate"],
            error,
        );
        form.setFields(fieldErrors);
    }, [error, form]);

    return (
        <Form
            form={form}
            name="base"
            className={classNames(!hidden && "d-flex flex-column overflow-hidden")}
            hidden={hidden}
        >
            <div className="overflow-auto flex-grow-1 mb-4">
                <Field
                    name="effectiveDate"
                    label={t("effective-date")}
                    rules={[
                        {
                            required: true,
                            message: t("required-effective-date")
                        }
                    ]}
                    className="d-flex flex-column w-100"
                >
                    <DateInput placeholderText={t("effective-date")} />
                </Field>

                <hr className="my-4" />

                <BaseSettingsFormContent
                    mode={mode}
                    disabled={disabled || !isMasterCompany}
                />

                <hr className="my-4" />

                <Field
                    name="canWaive"
                    label={t("allow-employee-to-waive-premium")}
                    valuePropName="checked"
                >
                    <Switch
                        onChange={(e) => {
                            const { checked } = e.target;
                            if (checked) {
                                return;
                            }
                            const rules = form.getFieldValue("rules");
                            form.setFieldValue(
                                "rules",
                                rules.map(rule => {
                                    return omit(rule, ["waivePayCode"]);
                                }),
                            );
                        }}
                    />
                </Field>

                <Field
                    name="canManagerWaive"
                    label={t("allow-manager-to-waive-premium-for-employee")}
                    valuePropName="checked"
                >
                    <Switch
                        onChange={(e) => {
                            const { checked } = e.target;
                            if (checked) {
                                return;
                            }
                            const rules = form.getFieldValue("rules");
                            form.setFieldValue(
                                "rules",
                                rules.map(rule => {
                                    return omit(rule, ["managerWaivePayCode"]);
                                }),
                            );
                        }}
                    />
                </Field>

                <hr className="my-4" />

                <div>
                    <Field name="shouldWorkBeContinuous" valuePropName="checked">
                        {({ checked, onChange }) => {
                            // wording is opposite to the value of the switch
                            return (
                                <Switch
                                    checked={!checked}
                                    onChange={(e) => onChange(!e.target.checked)}
                                >
                                    {t("evaluate-meal-requirements-across-the-full-workday")}
                                </Switch>
                            );
                        }}
                    </Field>
                    <p>
                        {t("combine-all-worked-time-within-the-defined-workday-including-split-shifts-and-multiple-work-periods-when-determining-meal-requirements-waivers-violations-and-premiums")}
                    </p>
                </div>

                <hr className="my-4" />

                <Field
                    name="maxPremiumCount"
                    label={t("how-should-multiple-meal-violations-in-the-same-workday-be-handled")}
                >
                    {({ value, onChange }) => {
                        return (
                            <RadioGroup value={value} onChange={onChange}>
                                <div className="d-flex flex-column gap-y-4">
                                    <RadioGroup.Option value="unlimited" className="d-flex align-items-start gap-y-2 cursor-pointer">
                                        {({ checked }) => {
                                            return (
                                                <>
                                                    <Checkbox checked={checked} />
                                                    <div>
                                                        <RadioGroup.Label as="p" className="mb-0 text-darker">
                                                            {t("generate-and-pay-a-premium-for-each-violation")}
                                                        </RadioGroup.Label>
                                                    </div>
                                                </>
                                            );
                                        }}
                                    </RadioGroup.Option>

                                    <RadioGroup.Option value="one-per-shift" className="d-flex align-items-start gap-y-2 cursor-pointer">
                                        {({ checked }) => {
                                            return (
                                                <>
                                                    <Checkbox checked={checked} />
                                                    <div>
                                                        <RadioGroup.Label as="p" className="mb-0 text-darker">
                                                            {t("generate-each-violation-but-pay-only-one-meal-premium-per-shift")}
                                                        </RadioGroup.Label>
                                                        <RadioGroup.Description as="p" className="mb-0">
                                                            {t("each-missed-late-or-shortened-meal-period-will-be-recorded-separately-for-compliance-reporting-acknowledgement-and-audit-purposes-however-only-one-meal-premium-will-be-payable-for-the-shift")}
                                                        </RadioGroup.Description>
                                                    </div>
                                                </>
                                            );
                                        }}
                                    </RadioGroup.Option>

                                    <RadioGroup.Option value="one-per-day" className="d-flex align-items-start gap-y-2 cursor-pointer">
                                        {({ checked }) => {
                                            return (
                                                <>
                                                    <Checkbox checked={checked} />
                                                    <div>
                                                        <RadioGroup.Label as="p" className="mb-0 text-darker">
                                                            {t("generate-each-violation-but-pay-only-one-meal-premium-per-workday")}
                                                        </RadioGroup.Label>
                                                        <RadioGroup.Description as="p" className="mb-0">
                                                            {t("each-missed-late-or-shortened-meal-period-will-be-recorded-separately-for-compliance-reporting-acknowledgement-and-audit-purposes-however-only-one-meal-premium-will-be-payable-for-the-workday")}
                                                        </RadioGroup.Description>
                                                    </div>
                                                </>
                                            );
                                        }}
                                    </RadioGroup.Option>
                                </div>
                            </RadioGroup>
                        );
                    }}
                </Field>
            </div>

            <div className="d-flex justify-content-end">
                <Button
                    onClick={close}
                    className="btn-round btn-icon shadow-none border btn btn-secondary btn-sm"
                >
                    {t("cancel")}
                </Button>

                <Button
                    type="submit"
                    className="btn-dark btn-sm shadow-none"
                >
                    {t("continue")}
                </Button>
            </div>
        </Form >
    );
}

export default Base;